import { isServer } from '../../settings';
import { suspenseAdapter } from './adapter';
import { getContextFromWindow } from '../context';
import { getAccessToken } from 'services/auth';

const cache = {};

const preloadedState = getContextFromWindow().state || {};
export const defaultCacheMaxAge = isServer ? 9999 : 1000 * 30;

const serializeArgs = (args) => {
  const arr = Object.entries(args || {}).map(
    ([key, value]) =>
      `${key}=${
        typeof value === 'string' && value.length > 64 ? value.slice(0, 64) + '...' : value
      }`,
  );
  arr.sort();
  return arr.join(',');
};
export const cacheKey = (service, arg) => {
  const serviceName = service.name;
  const parameters = serializeArgs(arg);
  return `${serviceName}-${parameters}`;
};
const clearLoaders = (loaders, before) =>
  Object.entries(loaders).reduce(
    (acc, [k, loader]) => (loader.timestamp < before ? acc : { ...acc, [k]: loader }),
    {},
  );

const withTokenRefresh = (service, arg, accessToken) => {
  if (!accessToken) {
    return service(arg);
  }
  if (isServer) {
    return service({ ...arg, accessToken });
  }
  return new Promise((resolve, reject) =>
    getAccessToken()
      .then((token) => {
        service({ ...arg, accessToken: token })
          .then(resolve)
          .catch(reject);
      })
      .catch(reject),
  );
};

export const makeLoader =
  (requestId, metrics, service, accessToken, state = {}, maxAge = defaultCacheMaxAge) =>
  (arg = {}) => {
    const key = cacheKey(service, arg);

    // share results between requests and distinguish preload state (p) from app rendering scope (s).
    const requestKey = requestId === '-' ? 's' : 'p';
    const loadersPerRequest = cache[key] || {};
    const loader = loadersPerRequest[requestKey] || {};

    const now = Date.now();
    if (!loader.func || loader.timestamp < now - maxAge + 3000) {
      loader.timestamp = now;
      const preloaded = preloadedState[key];

      // preloaded is window.__DUMPERT_CONTEXT__.state (can be true in client browser only)
      if (
        preloaded &&
        preloaded.timestamp > now - maxAge &&
        preloaded.data &&
        !preloaded.data.error
      ) {
        loader.func = () => preloaded.data;
      } else {
        // sate is a result of call before render if not null it's a render time (can be not null on server only)
        const result = (state && state[key] && state[key].data) || null;

        // doApiCall is a promise of api call (client SPA update or server preload call)
        const doApiCall =
          !isServer || requestId !== '-' ? withTokenRefresh(service, arg, accessToken) : () => {};

        // measure response time (server only)
        if (metrics && !result) {
          const endTimer = metrics.apiAsyncCallDuration.startTimer();
          const serviceName = service.name;
          const captureResult = ({ success = true }) => endTimer({ serviceName, success });
          doApiCall.then(captureResult, () => captureResult({ success: false }));
        }

        // if requestId is '-' then makeLoader is called from the hook useDataLoader while rendering react App on server or client.
        // when react App is rendered on server (isServer is true), no api calls are allowed, because rendering is synchronous,
        // response will not be handled and as a result placeholder loading dots will always be rendered.
        loader.func = isServer
          ? requestId !== '-'
            ? () => ({ promise: doApiCall })
            : () => result || { error: new Error(`${key} is not in route loader`) }
          : suspenseAdapter(doApiCall);
      }
      loadersPerRequest[requestKey] = loader;
      cache[key] = loadersPerRequest;
      Object.keys(cache).forEach((k) => {
        cache[k] = clearLoaders(cache[k], now - defaultCacheMaxAge);
      });
    }

    return loader.func();
  };
